import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  HelpOutline,
  ExpandMore,
  Folder,
  Dashboard,
  CloudUpload,
  Edit,
  ModelTraining,
  SwapHoriz,
  Assessment,
  Settings,
  People,
  Person,
  CheckCircle,
  ArrowRight,
  LooksOne,
  LooksTwo,
  Looks3,
  Looks4,
  Looks5,
  Looks6,
} from '@mui/icons-material';
import MainLayout from '../components/Layout/MainLayout';

const stepIcons = [LooksOne, LooksTwo, Looks3, Looks4, Looks5, Looks6];

const Section = ({ icon: Icon, iconColor, title, subtitle, children, defaultExpanded }) => {
  const theme = useTheme();
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ borderRadius: '12px !important', mb: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Icon sx={{ fontSize: 28, color: iconColor || theme.palette.primary.main }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>{title}</Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

const StepList = ({ steps }) => (
  <List dense disablePadding>
    {steps.map((step, i) => {
      const StepIcon = stepIcons[i] || ArrowRight;
      return (
        <ListItem key={i} sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <StepIcon color="primary" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={step.title}
            secondary={step.desc}
            primaryTypographyProps={{ fontWeight: 500 }}
          />
        </ListItem>
      );
    })}
  </List>
);

const Tip = ({ children }) => {
  const theme = useTheme();
  return (
    <Box sx={{
      mt: 2, p: 2, borderRadius: 2,
      bgcolor: alpha(theme.palette.info.main, 0.08),
      borderLeft: `4px solid ${theme.palette.info.main}`,
    }}>
      <Typography variant="body2"><strong>Tip:</strong> {children}</Typography>
    </Box>
  );
};

const Help = () => {
  const theme = useTheme();

  return (
    <MainLayout>
      <Container maxWidth="md">
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <HelpOutline sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Typography variant="h3" fontWeight={700}>User Guide</Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Learn how to use Digital Spectra for spectroscopic data analysis, AI modelling, and calibration transfer.
          </Typography>
        </Box>

        {/* Quick Start */}
        <Card sx={{ mb: 4, borderRadius: 3, border: `2px solid ${theme.palette.primary.main}` }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Quick Start
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Follow these steps to go from raw spectra to trained AI models:
            </Typography>
            <StepList steps={[
              { title: 'Create a project', desc: 'Go to Projects and create a new project.' },
              { title: 'Configure sensors & labels', desc: 'In Settings, add your spectroscopy sensors and define labels (numeric or categorical).' },
              { title: 'Import data', desc: 'Upload CSV files in the Dataset page.' },
              { title: 'Label your samples', desc: 'Use the Editor to assign labels to your spectra.' },
              { title: 'Train AI models', desc: 'Go to AI Models, select a label, sensor, and model type, then train.' },
              { title: 'Predict & report', desc: 'Use Reports to run predictions on new samples.' },
            ]} />
          </CardContent>
        </Card>

        {/* Sections */}
        <Section
          icon={Folder}
          title="Projects"
          subtitle="Create and manage your spectroscopy projects"
          defaultExpanded
        >
          <Typography variant="body2" paragraph>
            A project is the top-level container for all your data, sensors, labels, models, and reports.
          </Typography>
          <StepList steps={[
            { title: 'Create a project', desc: 'Click "New Project" on the Projects page. Give it a name and description.' },
            { title: 'Access your project', desc: 'Click a project card to enter its workspace. The sidebar will show all available sections.' },
          ]} />
          <Tip>
            You can also collaborate with other users. Add collaborators in the project Settings page by entering their email address.
          </Tip>
        </Section>

        <Section
          icon={Dashboard}
          title="Dashboard"
          subtitle="Overview of your project data at a glance"
        >
          <Typography variant="body2" paragraph>
            The Dashboard shows a summary of your project: how many samples, sensors, labels, and models you have. Use it to quickly assess the state of your project.
          </Typography>
        </Section>

        <Section
          icon={CloudUpload}
          title="Dataset"
          subtitle="Import, review, and manage spectral data"
        >
          <Typography variant="body2" paragraph>
            The Dataset page is where you upload and review your spectral data.
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Uploading Data</Typography>
          <StepList steps={[
            { title: 'Select a sensor', desc: 'Choose which sensor the data belongs to.' },
            { title: 'Upload a file', desc: 'Upload a CSV/DB file. On the Universial sensor, the first row should contain wavelength headers and each subsequent row is a sample, the first column being "Names" as the sample name.' },
            { title: 'Review', desc: 'After upload, you will see an interactive plot of all spectra. Click on a spectrum in the plot to select it for removal if needed.' },
            { title: 'Save to park', desc: 'Click "Save to Park" to store the imported samples in the Parked state, ready for labelling.' },
          ]} />
          <Tip>
            You can click directly on a spectrum in the interactive plot to remove outliers before saving.
          </Tip>
        </Section>

        <Section
          icon={Edit}
          title="Editor"
          subtitle="Label and curate your spectral samples"
        >
          <Typography variant="body2" paragraph>
            The Editor lets you assign labels to your samples. Select a sensor and label, then use the table to tag each sample.
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Labelling Workflow</Typography>
          <StepList steps={[
            { title: 'Select a sensor and label', desc: 'Use the dropdowns to choose the sensor and the label you want to assign values for.' },
            { title: 'Assign values', desc: 'For categorical labels, pick a class from the dropdown. For numeric labels, enter a numeric value. Another way is import a csv file in bulk, where each row corresponds to a sample and each column to a label. First column must be "Names" and subsequent columns are labels.' },
            { title: 'Save', desc: 'Samples with labels are moved to the "Labeled" status, making them available for model training.' },
          ]} />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Sample Statuses</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip label="Parked" size="small" color="default" />
              <Chip label="Labeled" size="small" color="primary" />
              <Chip label="Cortex" size="small" color="secondary" />
              <Chip label="Not OK" size="small" color="error" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>Parked</strong> = imported but unlabelled. <strong>Labeled</strong> = has labels assigned, ready for training.
              <strong> Cortex</strong> = labels from accepted AI predictions (self-learning). <strong>Not OK</strong> = excluded from analysis.
            </Typography>
          </Box>
        </Section>

        <Section
          icon={ModelTraining}
          iconColor={theme.palette.success.main}
          title="AI Models"
          subtitle="Train, evaluate, and manage predictive models"
        >
          <Typography variant="body2" paragraph>
            Train machine learning models on your labelled spectra. Supports classification (categorical labels) and regression (numeric labels).
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Model Types</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label="Linear (PLS/LDA)" size="small" variant="outlined" />
            <Chip label="Non-Linear (SVM/RF)" size="small" variant="outlined" />
            <Chip label="TabNet" size="small" variant="outlined" />
            <Chip label="1D CNN" size="small" variant="outlined" />
            <Chip label="Multi-Block CNN" size="small" variant="outlined" />
          </Box>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Training a Model</Typography>
          <StepList steps={[
            { title: 'Select label & sensor', desc: 'Choose the label and sensor to train on.' },
            { title: 'Pick a model type', desc: 'Choose from Linear, Non-Linear, TabNet, 1D CNN, or Multi-Block CNN.' },
            { title: 'Choose data source', desc: 'Train on Labeled data, Cortex data, or both.' },
            { title: 'Train', desc: 'Click "Train" and wait for the model to finish. You will see cross-validation and test-set performance plots.' },
          ]} />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Versioning & Self-Learning</Typography>
          <Typography variant="body2" paragraph>
            Each model has a version number. When you retrain, a new version is created. If it performs better, it is automatically promoted to the active version; otherwise the previous version is kept.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Self-learning:</strong> When you accept AI predictions in the Reports page, the predicted labels are saved as
            "Cortex" data. If enough new Cortex samples accumulate, the system automatically retrains the model and promotes the new version if it improves.
          </Typography>
          <Tip>
            Use the toggle button on each model card to activate/deactivate model versions. Only one version per label/sensor pair can be active at a time.
          </Tip>
        </Section>

        <Section
          icon={SwapHoriz}
          iconColor={theme.palette.warning.main}
          title="Calibration Transfer"
          subtitle="Transfer spectra between sensors"
        >
          <Typography variant="body2" paragraph>
            Calibration transfer maps spectra from a source (slave) sensor to match a target (master) sensor, so models trained on one instrument can be applied to another.
          </Typography>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Methods</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label="Bias Correction" size="small" variant="outlined" />
            <Chip label="Direct Standardization (DS)" size="small" variant="outlined" />
            <Chip label="Piecewise DS (PDS)" size="small" variant="outlined" />
            <Chip label="Linear (Ridge)" size="small" variant="outlined" />
            <Chip label="ANN (Conv1D + BiLSTM)" size="small" variant="outlined" />
          </Box>

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>How It Works</Typography>
          <StepList steps={[
            { title: 'Prepare transfer standards', desc: 'Measure the same samples on both sensors. Samples are auto-paired by matching their names.' },
            { title: 'Select source & target sensor', desc: 'Choose which sensor is the source (to be corrected) and which is the target (master).' },
            { title: 'Choose a method and train', desc: 'Select one of the 5 available methods. The system trains on 70% of paired samples and evaluates on the remaining 30%.' },
            { title: 'Apply the transfer', desc: 'Use a trained transfer model to transform new spectra from the source sensor. You can preview the results or save them as new samples on the target sensor.' },
          ]} />
          <Tip>
            For small differences between sensors, Bias Correction or Linear methods work well. For complex non-linear differences, try the ANN method.
          </Tip>
        </Section>

        <Section
          icon={Assessment}
          iconColor={theme.palette.error.main}
          title="Reports & Exploratory Data Analysis"
          subtitle="Explore data, run predictions, and review AI results"
        >
          <Typography variant="body2" paragraph>
            The Reports page combines <strong>Exploratory Data Analysis (EDA)</strong> tools with <strong>AI prediction</strong> capabilities.
            Use it to visualize your spectral data, detect outliers, and run predictions with trained models.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Exploratory Data Analysis</Typography>
          <Typography variant="body2" paragraph>
            Four interactive plot types are available to explore and understand your spectral data. All plots support
            filtering by <strong>label</strong> (with optional category choice) or by <strong>sample status</strong> (Labeled, Cortex, Parked, Legacy).
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Chip label="PCA" size="small" color="primary" />
            <Chip label="UMAP" size="small" color="secondary" />
            <Chip label="Q-T Plot" size="small" color="warning" />
            <Chip label="Mahalanobis" size="small" color="error" />
          </Box>

          <List dense disablePadding>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="PCA (Principal Component Analysis)"
                secondary="Projects high-dimensional spectra onto PC1 vs PC2. Great for visualising cluster separation between classes and spotting outliers."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="UMAP (Uniform Manifold Approximation)"
                secondary="Non-linear dimensionality reduction that preserves local structure. Parameters (n_neighbors, min_dist, metric) are automatically optimised using silhouette score."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="Q-T Plot (Q Residuals vs Hotelling's T²)"
                secondary="Quality control chart. Samples outside the control limits (red dashed lines) may be outliers. Q residuals measure reconstruction error; T² measures distance from the PCA model centre."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="Mahalanobis Distance"
                secondary="Measures each sample's distance from the data centre accounting for correlations. Uses robust covariance estimation (MinCovDet). High distances indicate potential outliers."
              />
            </ListItem>
          </List>

          <Tip>
            All plots apply automatic preprocessing: SNV normalisation, Savitzky-Golay derivative (for spectroscopic sensors), and mean centering before computing projections.
          </Tip>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Prediction Overlay</Typography>
          <Typography variant="body2" paragraph>
            Select unlabelled (Parked) samples to overlay on any EDA plot as prediction points (shown in red).
            This lets you visually assess where new samples fall relative to your training data before running AI predictions.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={600} gutterBottom>AI Predictions</Typography>
          <StepList steps={[
            { title: 'Select a model', desc: 'Choose the active AI model you want to use for predictions.' },
            { title: 'Select samples', desc: 'Choose which samples to predict (typically Parked samples without labels).' },
            { title: 'Run prediction', desc: 'Click "Predict" to generate results. You will see predictions listed with confidence information.' },
            { title: 'Accept predictions', desc: 'If you trust the predictions, click "Accept" to save them as Cortex labels. This feeds the self-learning loop.' },
          ]} />
        </Section>

        <Section
          icon={Settings}
          title="Project Settings"
          subtitle="Configure sensors, labels, and team access"
        >
          <Typography variant="body2" paragraph>
            The Settings page lets you manage three aspects of your project:
          </Typography>
          <List dense disablePadding>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="Sensors"
                secondary="Add, edit, or remove spectroscopy sensors. Each sensor requires a manufacturer and model."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="Labels"
                secondary="Define classification labels (Categorical with choices, or Numeric for regression values)."
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><ArrowRight color="primary" /></ListItemIcon>
              <ListItemText
                primary="Collaborators"
                secondary="Invite other users by email. Collaborators have full access to all project data and models."
              />
            </ListItem>
          </List>
        </Section>

        <Section
          icon={People}
          title="Collaboration"
          subtitle="Working with your team"
        >
          <Typography variant="body2" paragraph>
            Digital Spectra supports team collaboration on projects. A project has one <strong>Owner</strong> and
            can have multiple <strong>Collaborators</strong>.
          </Typography>
          <List dense disablePadding>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Collaborators can view and edit all data, train models, and run predictions." />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Only the project owner can add or remove collaborators." />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon sx={{ minWidth: 36 }}><CheckCircle color="success" fontSize="small" /></ListItemIcon>
              <ListItemText primary="Collaborators see shared projects alongside their own in the Projects list." />
            </ListItem>
          </List>
        </Section>

        <Section
          icon={Person}
          title="Profile"
          subtitle="Manage your account"
        >
          <Typography variant="body2" paragraph>
            Access your profile from the avatar menu in the top-right corner. You can update your first/last name and change your password.
          </Typography>
        </Section>

        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Digital Spectra — Spectroscopic Data Analysis Platform
          </Typography>
        </Box>
      </Container>
    </MainLayout>
  );
};

export default Help;
